import {
  Component,
  EventEmitter,
  Input,
  Output,
  ElementRef,
  inject,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Tag } from '@6grain/masofa-monolith-api-client/dictionaries';
import { OverlayModule } from '@angular/cdk/overlay';
import { TranslateService } from '@ngx-translate/core';
import { TagModalComponent } from '@app/components/tag-modal/tag-modal.component';
import { TooltipModule } from 'primeng/tooltip';
import { TagDictionaryItem, TagDictionaryService } from '@app/services/tag-dictionary.service';

@Component({
  selector: 'app-tag-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, OverlayModule, TagModalComponent, TooltipModule],
  templateUrl: './tag-selector.component.html',
  styleUrls: ['./tag-selector.component.scss'],
})
export class TagSelectorComponent implements OnInit, OnChanges {
  @Input() tags: Tag[] = [];
  @Input() selectedTagIds: string[] = [];
  @Input() placeholder: string = 'Select tags';

  @Output() selectionChange = new EventEmitter<string[]>();
  @Output() tagCreated = new EventEmitter<Tag>();

  @ViewChild('trigger', { read: ElementRef }) trigger!: ElementRef;

  isOpen = false;
  searchTerm = '';
  triggerWidth = 0;
  isCreateModalOpen = false;
  createModalInitialName = '';
  existingTags: TagDictionaryItem[] = [];

  private translate = inject(TranslateService);
  private tagDictionaryService = inject(TagDictionaryService);

  get filteredTags(): Tag[] {
    if (!this.searchTerm.trim()) return this.tags;
    const term = this.searchTerm.toLowerCase();
    return this.tags.filter((t) => t.name?.toLowerCase().includes(term));
  }

  canShowCreateButton(): boolean {
    const name = this.searchTerm.trim();
    if (!name) {
      return false;
    }
    return !this.existingTags.some((t) => t.name?.toLowerCase() === name.toLowerCase());
  }

  ngOnInit(): void {
    this.loadTags();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tags'] && Array.isArray(this.tags)) {
      this.syncIncomingTags();
    }
  }

  toggleDropdown(): void {
    if (!this.isOpen) {
      const rect = this.trigger.nativeElement.getBoundingClientRect();
      this.triggerWidth = rect.width;
      this.isOpen = true;
    } else {
      this.isOpen = false;
    }
  }

  isSelected(id: string | undefined): boolean {
    return id ? this.selectedTagIds.includes(id) : false;
  }

  toggleTag(id: string | undefined, event?: Event): void {
    event?.stopPropagation();
    if (!id) return;

    let newSelection = [...this.selectedTagIds];
    if (this.isSelected(id)) {
      newSelection = newSelection.filter((tid) => tid !== id);
    } else {
      newSelection.push(id);
    }

    this.selectedTagIds = newSelection;
    this.selectionChange.emit(newSelection);
  }

  removeTag(id: string, event: Event): void {
    event.stopPropagation();
    const newSelection = this.selectedTagIds.filter((tid) => tid !== id);
    this.selectedTagIds = newSelection;
    this.selectionChange.emit(newSelection);
  }

  getTagName(id: string): string {
    return this.tags.find((t) => t.id === id)?.name || this.translate.instant('Unknown');
  }

  createTag(): void {
    // Deprecated inline creation
  }

  openCreateModal(event?: Event): void {
    event?.stopPropagation();
    if (this.isOpen) {
      this.isOpen = false;
    }
    this.createModalInitialName = this.searchTerm.trim();
    this.isCreateModalOpen = true;
  }

  handleModalSaved(tag: TagDictionaryItem): void {
    const mapped: Tag = { id: tag.id, name: tag.name };
    this.existingTags = this.mergeDistinctTags([tag]);
    this.tags = this.existingTags.map((t) => ({ id: t.id!, name: t.name || '' }));

    if (!this.selectedTagIds.includes(tag.id)) {
      const newSelection = [...this.selectedTagIds, tag.id];
      this.selectedTagIds = newSelection;
      this.selectionChange.emit(newSelection);
    }

    this.tagCreated.emit(mapped);
    this.searchTerm = '';
    this.isCreateModalOpen = false;
  }

  private loadTags(): void {
    this.tagDictionaryService.getAllTags().subscribe({
      next: (tags) => {
        this.existingTags = this.mergeDistinctTags(tags);
        this.tags = this.existingTags.map((t) => ({ id: t.id!, name: t.name || '' }));
      },
      error: (err) => console.error('Error loading tags', err),
    });
  }

  private syncIncomingTags(): void {
    const incoming: TagDictionaryItem[] =
      this.tags?.map((t) => ({ id: t.id!, name: t.name || '' } as TagDictionaryItem)) ?? [];
    this.existingTags = this.mergeDistinctTags(incoming);
    this.tags = this.existingTags.map((t) => ({ id: t.id!, name: t.name || '' }));
  }

  private mergeDistinctTags(additional: TagDictionaryItem[]): TagDictionaryItem[] {
    const map = new Map<string, TagDictionaryItem>();
    this.existingTags.forEach((t) => map.set(t.id, t));
    additional.forEach((t) => {
      if (t.id) {
        const existing = map.get(t.id);
        map.set(t.id, { ...existing, ...t });
      }
    });
    return Array.from(map.values());
  }
}
